import React, { useContext, useState } from "react";
import { Box, TextField, IconButton, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StockroomRequestContext } from "../context/StockroomRequestContext";
import { StyledTableBody, StyledTableRow, UXDataTableWithoutBody } from "../../../components/UXDataTable";
import { StyledTableCellSmall } from "../styles";
import { convertDateFormat, exportToExcel, DatePickerKeyDownEvent } from "../../../global";
import { GlobalButton } from "../../styles";

const colsStkTracker = [
  { id: 'stockroomNo', key: 'stockroomNo', label: 'Stock #' },
  { id: 'description', key: 'description', label: 'Description' },
  { id: 'actionDate', key: 'actionDate', label: 'Action Date', type: 'dateTime' },
  { id: 'actionType', key: 'actionType', label: 'Action Type' },
  { id: 'actionBy', key: 'actionBy', label: 'Action By' },
  { id: 'actionText', key: 'actionText', label: 'Action' },
];

const DownloadExcel = ({list, cols, fields, name}) => {
  return <IconButton onClick={e => {
    e.preventDefault();
    exportToExcel(list.map(item => {
      const object = {};
      cols.forEach((col, index) => {
        object[col] = fields[index].type === 'dateTime' ? convertDateFormat(item[fields[index].key]) : item[fields[index].key]
      })
      return object;
    }), name, false)
  }}>
    <FileDownloadIcon sx={{ m: 1 }} style={{ cursor: "pointer" }} />
  </IconButton>
}

export const StkTrackerReport = ({ canEdit, selectedSite }) => {
  const [stockRoomNumber, setStockRoomNumber] = useState('')

  const day = new Date().getDate()
  const month = new Date().getMonth()
  const year = new Date().getFullYear()

  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(new Date(year, month, day))
  const { getStockroomReports } = useContext(StockroomRequestContext);
  const [chargesReportRowsPerPage, setChargesReportRowsPerPage] = useState(10);
  const [chargesReportPage, setChargesReportPage] = useState(0);
  const [chargesReport, setChargesReport] = useState([]);
  const [isDataloading, setIsDataLoading] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  const handleCreateReport = async () => {
    setIsDataLoading(true);
    setChargesReport([]);

    const payload = {
      Report: "J",
      StockroomNo: stockRoomNumber,
      StartDate: dateFrom,
      EndDate: dateTo,
      SiteName: selectedSite
    };

    let report = await getStockroomReports(payload);
    setChargesReport(report ?? []);
    setIsDataLoading(false);
  };

  const handleChangeReportRowsPerPage = (event) => {
    setChargesReportRowsPerPage(parseInt(event.target.value, 10));
    setChargesReportPage(0);
  };

  const handleBuildReportPageChange = (event, page) => {
    setChargesReportPage(page);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
    return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
  }

  return (
    <Box>
      <Box display="flex" gap={4} marginBottom={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              disabled={!canEdit}
              label="Search From"
              maxDate={new Date()}
              value={dateFrom}
              onChange={e => setDateFrom(e)}
              renderInput={(params) => <TextField onKeyDown={DatePickerKeyDownEvent} size="small" {...params} />}
            />
            <DesktopDatePicker
              disabled={!canEdit}
              label="To"
              value={dateTo}
              minDate={dateFrom}
              maxDate={new Date()}
              onChange={e => setDateTo(e)}
              renderInput={(params) => <TextField onKeyDown={DatePickerKeyDownEvent} size="small" {...params} />}
            />
          </LocalizationProvider>
          <TextField
            disabled={!canEdit}
            onChange={e => setStockRoomNumber(e.target.value)}
            value={stockRoomNumber}
            label="Stockroom #"
            size="small"
          />
        </Box>

        <Box display="flex" alignItems="center">
          <GlobalButton
            disabled={!canEdit || !dateTo || !dateFrom || isDataloading || !selectedSite}
            variant="contained"
            onClick={handleCreateReport}
          >
            Create report
          </GlobalButton>
          {isDataloading && 
            <CircularProgress style={{textTransform: "none", marginLeft:"15px"}}>
            </CircularProgress>
          }
          {chargesReport?.length > 0 && !isDataloading &&
            <DownloadExcel 
              list={chargesReport} 
              cols={colsStkTracker.map(c => c.label)} 
              fields={colsStkTracker} 
              name={'Stk Tracker Report'}
            /> 
          }          
        </Box>
      </Box>
      <Box>
        <UXDataTableWithoutBody
          tableWidth="100%"
          cols={colsStkTracker}
          tableId="chargesTable"
          rowLength={chargesReport?.length}
          enableSorteable={true}
          enablePaging={true}
          rowsPerPage={chargesReportRowsPerPage}
          page={chargesReportPage}
          isDataLoading={isDataloading}
          handleChangePage={handleBuildReportPageChange}
          handleChangeRowsPerPage={handleChangeReportRowsPerPage}
          noDataFoundMessage={"No charges found"}
          order={order}
          orderBy={orderBy}
          handleRequestSort={handleRequestSort}
        >
          <StyledTableBody>
            {chargesReport?.length > 0 &&
              stableSort(chargesReport, getComparator(order, orderBy)).slice(
                chargesReportPage * chargesReportRowsPerPage,
                chargesReportPage * chargesReportRowsPerPage + chargesReportRowsPerPage
              ).map((item) => {                
                  return (
                  [
                    <StyledTableRow>
                      {colsStkTracker.map(field => {
                        return <StyledTableCellSmall>
                            { field.type === 'dateTime' ? convertDateFormat(item[field.key]) : item[field.key]}
                          </StyledTableCellSmall>
                        }
                      )}
                    </StyledTableRow>
                  ]
                );
                })}
          </StyledTableBody>
        </UXDataTableWithoutBody>
      </Box>
    </Box>
  )
}